/**
 * 77Skin — Cart Drawer Override
 * Forces all cart interactions through the slide-out drawer.
 *
 * 1. Intercepts every add-to-cart form submission → AJAX add → open drawer
 * 2. Hijacks /cart links → open drawer instead
 * 3. Suppresses the default cart-notification popup
 * 4. Re-attaches overlay listener after AJAX re-renders
 */

(function () {
  'use strict';

  /* -------------------------------------------------- */
  /*  HELPERS                                           */
  /* -------------------------------------------------- */

  function getCartDrawer() {
    return document.querySelector('cart-drawer');
  }

  function fetchConfig(type = 'json') {
    return {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/javascript',
      },
    };
  }

  function getSectionsToRender() {
    return [
      { id: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble' },
    ];
  }

  function getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector)?.innerHTML ?? '';
  }

  /* -------------------------------------------------- */
  /*  1. INTERCEPT ADD-TO-CART FORMS                    */
  /* -------------------------------------------------- */

  function handleAddToCart(evt) {
    const form = evt.target.closest('form[action$="/cart/add"]');
    if (!form) return;

    evt.preventDefault();
    evt.stopPropagation();

    const submitBtn = form.querySelector('[type="submit"]');
    if (submitBtn) {
      submitBtn.setAttribute('aria-disabled', 'true');
      submitBtn.classList.add('loading');
    }

    const formData = new FormData(form);
    const body = JSON.stringify({
      items: [
        {
          id: parseInt(formData.get('id'), 10),
          quantity: parseInt(formData.get('quantity') || '1', 10),
          properties: Object.fromEntries(
            [...formData.entries()].filter(
              ([k]) => k !== 'id' && k !== 'quantity' && k !== 'form_type' && k !== 'utf8' && !k.startsWith('section')
            )
          ),
        },
      ],
      sections: getSectionsToRender().map((s) => s.id),
      sections_url: window.location.pathname,
    });

    fetch(window.Shopify?.routes?.root
        ? `${window.Shopify.routes.root}cart/add.js`
        : '/cart/add.js',
      { ...fetchConfig(), body }
    )
      .then((r) => {
        if (!r.ok) throw new Error(r.statusText);
        return r.json();
      })
      .then((data) => {
        const drawer = getCartDrawer();
        if (!drawer) {
          // Fallback: no cart-drawer element on the page
          window.location.href = '/checkout';
          return;
        }

        // If the response includes sections, update the drawer
        if (data.sections) {
          drawer.renderContents && typeof drawer.renderContents === 'function'
            ? drawer.renderContents({ id: data.items?.[0]?.key, sections: data.sections })
            : updateDrawerManually(data.sections, drawer);
        } else {
          // Re-fetch the drawer section to get fresh HTML
          refreshDrawerAndOpen(drawer);
        }
      })
      .catch((err) => {
        console.error('[77Skin Cart]', err);
        // On error still try to open drawer so user sees feedback
        const drawer = getCartDrawer();
        if (drawer) refreshDrawerAndOpen(drawer);
      })
      .finally(() => {
        if (submitBtn) {
          submitBtn.removeAttribute('aria-disabled');
          submitBtn.classList.remove('loading');
        }
      });
  }

  /** Manually patch drawer HTML when renderContents isn't available */
  function updateDrawerManually(sections, drawer) {
    getSectionsToRender().forEach((section) => {
      const target = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (target && sections[section.id]) {
        target.innerHTML = getSectionInnerHTML(sections[section.id], section.selector);
      }
    });

    // Re-bind overlay close
    const overlay = drawer.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', () => drawer.close());

    drawer.classList.remove('is-empty');
    openDrawer(drawer);
  }

  /** Fetch fresh drawer section and open */
  function refreshDrawerAndOpen(drawer) {
    fetch('/?sections=cart-drawer,cart-icon-bubble')
      .then((r) => r.json())
      .then((sections) => {
        updateDrawerManually(sections, drawer);
      })
      .catch(() => {
        // Even if refresh fails, try opening with stale content
        openDrawer(drawer);
      });
  }

  function openDrawer(drawer) {
    if (typeof drawer.open === 'function') {
      drawer.open();
    } else {
      // Manual open
      setTimeout(() => {
        drawer.classList.add('animate', 'active');
      });
      document.body.classList.add('overflow-hidden');
    }
  }

  // Capture add-to-cart at the document level (handles dynamic forms too)
// Capture add-to-cart at the document level
  // Skip forms inside <product-form> — Dawn's JS already handles those
  document.addEventListener('submit', function (evt) {
    const form = evt.target;
    if (
      form.tagName === 'FORM' &&
      form.getAttribute('action')?.includes('/cart/add') &&
      !form.closest('product-form')
    ) {
      handleAddToCart(evt);
    }
  });

  /* -------------------------------------------------- */
  /*  2. HIJACK /cart LINKS → OPEN DRAWER               */
  /* -------------------------------------------------- */

  document.addEventListener('click', function (evt) {
    const link = evt.target.closest('a[href]');
    if (!link) return;

    const href = link.getAttribute('href');

    // Match /cart but NOT /cart/add, /checkout, /cart/clear etc.
    if (
      href === '/cart' ||
      href === '/cart/' ||
      href.match(/^\/cart\/?(\?.*)?$/)
    ) {
      evt.preventDefault();
      evt.stopPropagation();

      const drawer = getCartDrawer();
      if (drawer) {
        openDrawer(drawer);
      }
    }
  });

  /* -------------------------------------------------- */
  /*  3. SUPPRESS CART-NOTIFICATION POPUP               */
  /* -------------------------------------------------- */

  // If Dawn's cart-notification exists, neuter its open() so the drawer is used instead
  const origDefine = customElements.define.bind(customElements);
  const _alreadyPatched = new Set();

  // Wait for cart-notification to register, then override
  function patchCartNotification() {
    const notificationEl = document.querySelector('cart-notification');
    if (notificationEl && !_alreadyPatched.has(notificationEl)) {
      _alreadyPatched.add(notificationEl);

      notificationEl.open = function () {
        // Instead of showing the notification, open the drawer
        const drawer = getCartDrawer();
        if (drawer) openDrawer(drawer);
      };

      notificationEl.renderContents = function (parsedState) {
        // Update the icon bubble count
        const bubble = document.getElementById('cart-icon-bubble');
        if (bubble && parsedState?.sections?.['cart-icon-bubble']) {
          bubble.innerHTML = getSectionInnerHTML(
            parsedState.sections['cart-icon-bubble']
          );
        }

        // Now open the drawer with fresh data
        const drawer = getCartDrawer();
        if (drawer) {
          if (parsedState?.sections?.['cart-drawer']) {
            updateDrawerManually(parsedState.sections, drawer);
          } else {
            refreshDrawerAndOpen(drawer);
          }
        }
      };
    }
  }

  // Patch on load and on every DOM change (handles lazy-loaded elements)
  patchCartNotification();
  new MutationObserver(patchCartNotification).observe(document.body, {
    childList: true,
    subtree: true,
  });

  /* -------------------------------------------------- */
  /*  4. PATCH product-form's fetch to include drawer   */
  /*     sections so renderContents gets the right data */
  /* -------------------------------------------------- */

  // Override the global fetch to inject cart-drawer section requests
  // when adding to cart, so Dawn's product-form gets drawer HTML
  const _origFetch = window.fetch;
  window.fetch = function (url, opts) {
    if (
      typeof url === 'string' &&
      url.includes('/cart/add') &&
      opts?.body
    ) {
      try {
        const parsed = JSON.parse(opts.body);
        if (parsed && !parsed.sections?.includes?.('cart-drawer')) {
          parsed.sections = parsed.sections || [];
          if (!parsed.sections.includes('cart-drawer'))
            parsed.sections.push('cart-drawer');
          if (!parsed.sections.includes('cart-icon-bubble'))
            parsed.sections.push('cart-icon-bubble');
          opts = { ...opts, body: JSON.stringify(parsed) };
        }
      } catch (_) {
        // Not JSON body — let it through
      }
    }
    return _origFetch.call(this, url, opts);
  };

  /* -------------------------------------------------- */
  /*  5. RE-ATTACH OVERLAY AFTER SECTION RE-RENDER      */
  /* -------------------------------------------------- */

  new MutationObserver(function () {
    const drawer = getCartDrawer();
    if (!drawer) return;
    const overlay = drawer.querySelector('#CartDrawer-Overlay');
    if (overlay && !overlay._s77bound) {
      overlay._s77bound = true;
      overlay.addEventListener('click', () => drawer.close());
    }
  }).observe(document.body, { childList: true, subtree: true });

})();